// MACHINERY V2 (Stroje) — "Zdieľať" for one service report (M2.5).
//
// The v2 face of components/ServiceReportShareDialog.tsx. Same URL, same
// clipboard write, same `linkCopied` success toast, same close-on-copy — only
// the shell changes (Radix instead of MUI; see TabDialog.tsx for why these four
// dialogs are rebuilt rather than reused).
//
// The share target is the PUBLIC report page: `${origin}/public/service-reports/
// ${number}` — verified against ServiceReportShareDialog.tsx:20, which builds
// exactly that string. Do not "improve" it into an app-internal URL: the point
// of the action is a link a non-user can open.
//
// The field is read-only and deliberately not focusable-for-edit: it exists so
// the user can see and hand-select the URL when the Clipboard API is missing.
// v1 silently does nothing when `navigator.clipboard` is absent (an insecure
// context, e.g. plain http); v2 keeps the dialog OPEN in that case instead of
// closing it as if the copy had worked.
//
// ABSENT AND REJECTED ARE TWO DIFFERENT FAILURES, AND BOTH ARE HANDLED NOW
// (M2.5 review finding C5). The paragraph above only reasoned about the API
// being ABSENT. `writeText` also REJECTS while present — the permission is
// dismissed, or the document is not focused, which is exactly the state a Radix
// dialog can leave the page in right after it takes focus. The promise had no
// `.catch`, so that produced no toast, no error, a dialog that just sat there,
// and an unhandled rejection in the console. The rejection now goes through
// ToastV2 (setError -> MachineryToastHost) and the dialog stays open with the
// URL selectable, which is the same "you can still do it by hand" exit as the
// absent case.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { useAppDispatch } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import { setError, setSuccess } from "../../../customerMachinerySlice";
import { ServiceReport } from "../../../models/ServiceReportsModel";
import TabDialog from "./TabDialog";

export interface SrShareDialogV2Props {
  report: ServiceReport;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SrShareDialogV2: React.FC<SrShareDialogV2Props> = ({ report, open, onOpenChange }) => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");

  const url = `${window.location.origin}/public/service-reports/${report.number}`;

  const copy = () => {
    if (!navigator.clipboard) return;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        dispatch(setSuccess(translate("myMachinery:linkCopied")));
        onOpenChange(false);
      })
      // C5: a denied or unfocused-document write must be spoken aloud, and the
      // dialog must stay open so the URL can still be selected by hand.
      .catch(() => dispatch(setError(translate("myMachinery:v2.tabs.sr.copyFailed"))));
  };

  return (
    <TabDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate("myMachinery:shareLink")}
      width={520}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={() => onOpenChange(false)}>
            {translate("common:buttons.close")}
          </button>
          <button
            type="button"
            className="btn btn-mod"
            onClick={copy}
            disabled={!navigator.clipboard}
          >
            {translate("myMachinery:copyLink")}
          </button>
        </>
      }
    >
      <input
        readOnly
        value={url}
        aria-label={translate("myMachinery:shareLink")}
        onFocus={(e) => e.currentTarget.select()}
        style={{
          width: "100%",
          height: 38,
          padding: "0 12px",
          borderRadius: 8,
          border: "1px solid var(--border)",
          background: "var(--bg-soft)",
          color: "var(--ink-2)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          boxSizing: "border-box",
        }}
      />
    </TabDialog>
  );
};

export default SrShareDialogV2;
